import { useCallback, useEffect, useRef, useState } from 'react'
import { useOrgs } from '../lib/orgs.js'
import { useApi } from '../lib/useApi.js'
import type { AuthorizationRequest, Project } from '../lib/api.js'
// The same list the API enforces. Imported rather than retyped, so a
// new scope cannot appear on this screen as a bare identifier.
import { oauthScopeWords } from '@nimblybase/contracts'
import { returnToOAuthClient } from '../lib/oauth-return.js'

/**
 * The consent screen — V1-14.
 *
 * This page IS the authorization endpoint. A client redirects a browser
 * here; the API cannot be that endpoint itself, because a top-level
 * navigation carries cookies rather than an Authorization header and so
 * could never reach a route guarded by one.
 *
 * What a person is deciding is narrow and worth putting in front of
 * them plainly: which application, and what it will be able to do. The
 * scope list is rendered from what the API returned, never from the URL
 * - the URL is the client's to write, and an application that asks for
 * one thing on screen and another in the request is the whole attack.
 */

export default function Authorize() {
  const api = useApi()
  const { active } = useOrgs()
  const reviewedOrg = useRef<string | null>(null)
  const reviewedSearch = useRef<string | null>(null)
  const reviewedQuery = useRef<string | null>(null)
  const reviewedParams = useRef<Record<string, string> | null>(null)
  const reviewedClientName = useRef('your application')
  const recorded = useRef(false)
  const search = window.location.search

  // A native host may have no project selector. The person can narrow its
  // workspace request here, but cannot widen a project pinned by the client.
  // Keep the client's original state/PKCE URL intact and re-review the chosen
  // project through the server before enabling any decision.
  const clientPinnedProject = new URLSearchParams(search).has('project_id')
  const [choice, setChoice] = useState<{ orgId: string; search: string; projectId: string } | null>(null)
  const chosenProject = !clientPinnedProject && choice?.orgId === active?.id && choice?.search === search ? choice.projectId : ''
  const params = new URLSearchParams(search)
  if (chosenProject) params.set('project_id', chosenProject)
  const reviewQuery = chosenProject ? `?${params}` : search
  const [projectChoices, setProjectChoices] = useState<{
    orgId: string; search: string; projects: Pick<Project, 'id' | 'name'>[]; failed: boolean
  } | null>(null)
  const [projectRetry, setProjectRetry] = useState(0)

  const [request, setRequest] = useState<(AuthorizationRequest & { reviewedSearch: string; reviewedQuery: string }) | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deciding, setDeciding] = useState(false)
  const [completion, setCompletion] = useState<{ approved: boolean; redirectTo: string; clientName: string } | null>(null)
  const [returnError, setReturnError] = useState<string | null>(null)

  const returnToClient = useCallback((url: string) => {
    setReturnError(null)
    try { returnToOAuthClient(url) }
    catch { setReturnError('The browser could not return to the application. Keep the application open and try returning again.') }
  }, [])

  const decide = useCallback(async (approved: boolean) => {
    if (recorded.current) return
    if (!reviewedParams.current || reviewedSearch.current !== window.location.search || reviewedQuery.current !== reviewQuery) {
      setError('The request changed. Review it again before deciding.')
      return
    }
    if (approved && reviewedOrg.current !== active?.id) {
      setError('The workspace changed. Review the request again before approving.')
      return
    }
    setDeciding(true)
    setError(null)
    const clientName = reviewedClientName.current
    try {
      const { redirect_to } = await api.decideAuthorization(reviewedParams.current, approved, reviewedOrg.current ?? '')
      recorded.current = true
      setDeciding(false)
      setCompletion({ approved, redirectTo: redirect_to, clientName })
      // Leaving our own origin for the client's. assign, not the router:
      // If navigation is blocked or stays here, the decision is still recorded.
      returnToClient(redirect_to)
    } catch (e) {
      setDeciding(false)
      setError(e instanceof Error ? e.message : 'Could not complete the request.')
    }
  }, [api, active?.id, reviewQuery, returnToClient])

  useEffect(() => {
    // A callback retry must not submit consent or mint another code. Switching
    // workspace after a recorded decision must not auto-submit it either.
    if (recorded.current && reviewedSearch.current === search) return
    recorded.current = false
    setCompletion(null)
    setReturnError(null)
    let cancelled = false
    api.describeAuthorization(reviewQuery)
      .then((r) => {
        if (cancelled) return
        const params = Object.fromEntries(new URLSearchParams(reviewQuery))
        const expectedProject = params.project_id
        if (expectedProject !== undefined && (typeof r.project?.id !== 'string'
          || r.project.id.toLowerCase() !== expectedProject.toLowerCase())) {
          throw new Error('The requested project restriction was not confirmed. Nothing was approved.')
        }
        reviewedOrg.current = r.organization_id
        reviewedClientName.current = r.client_name
        reviewedSearch.current = search
        reviewedQuery.current = reviewQuery
        reviewedParams.current = { ...params, ...(r.project ? { project_id: r.project.id } : {}) }
        setError(null)
        // Only the literal boolean false permits the first-party shortcut.
        // An absent or malformed policy must still present explicit consent.
        const reviewed = { ...r, reviewedSearch: search, reviewedQuery: reviewQuery, requires_consent: r.requires_consent !== false || Boolean(r.project) }
        setRequest(reviewed)
        // Only trusted first-party workspace requests may skip the screen.
        // Project access always needs an explicit review and decision.
        if (!reviewed.requires_consent) void decide(true)
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Invalid request.') })
    return () => { cancelled = true }
  }, [api, search, reviewQuery, decide])

  useEffect(() => {
    if (!active?.id || clientPinnedProject || !request?.requires_consent || request.organization_id !== active.id) return
    const orgId = active.id
    let cancelled = false
    api.listProjects()
      .then(({ projects }) => { if (!cancelled) setProjectChoices({ orgId, search, projects, failed: false }) })
      .catch(() => { if (!cancelled) setProjectChoices({ orgId, search, projects: [], failed: true }) })
    return () => { cancelled = true }
  }, [api, active?.id, clientPinnedProject, request?.requires_consent, request?.organization_id, search, projectRetry])

  if (error) {
    return (
      <div className="empty">
        <h1>This request cannot be completed</h1>
        <p className="muted">{error}</p>
        <p className="muted">
          If you attempted approval, this page cannot confirm the decision outcome.
          Check your connections in Connect agent before starting another request.
        </p>
      </div>
    )
  }

  if (completion) {
    return <div className="consent">
      <h1>{completion.approved ? 'Approval recorded' : 'Request cancelled'}</h1>
      <p>{completion.approved
        ? 'Return to the application to finish sign-in. This page cannot confirm whether the client connected.'
        : 'No access was approved by this request. You can close this tab.'}</p>
      <p className="muted">If this tab stays open, keep the application running and try returning again.</p>
      {returnError && <p role="alert">{returnError}</p>}
      <button type="button" className="btn" onClick={() => returnToClient(completion.redirectTo)}>
        Return to {completion.clientName}
      </button>
      <p><a href="/connect">Review your connections in Connect agent</a></p>
    </div>
  }

  if (!request || request.reviewedQuery !== reviewQuery || request.reviewedSearch !== search) return <div className="empty muted">Checking the request…</div>
  if (!request.requires_consent) return <div className="empty muted">Signing you in…</div>

  return (
    <div className="consent">
      <h1>{request.client_name} wants access to {request.project ? 'your project' : 'your workspace'}</h1>
      <p>Workspace: <strong>{active?.name ?? 'No workspace selected'}</strong></p>
      {!clientPinnedProject && <>
        <label htmlFor="consent-project">Connection access</label>
        <select id="consent-project" value={chosenProject}
          disabled={deciding || projectChoices?.orgId !== active?.id || projectChoices?.search !== search || projectChoices.failed}
          onChange={(e) => {
            // Invalidate the previous review immediately, including an old
            // button event before the new server response arrives.
            reviewedParams.current = null
            reviewedQuery.current = null
            if (active) setChoice({ orgId: active.id, search, projectId: e.target.value })
          }}>
          <option value="">This workspace (needed to create projects)</option>
          {projectChoices?.orgId === active?.id && projectChoices?.search === search
            && projectChoices.projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
        </select>
        <p className="muted small">Choose one existing project to update an app. Workspace access is needed to create new projects.</p>
        {projectChoices?.orgId === active?.id && projectChoices?.search === search && projectChoices.failed && <p role="alert">
          Could not load project choices. Retry before choosing project-only access.{' '}
          <button type="button" className="btn" onClick={() => setProjectRetry(n => n + 1)}>Retry project choices</button>
        </p>}
      </>}
      {request.project
        ? <><p>Project: <strong>{request.project.name}</strong></p><p className="muted small">Only this project is accessible. Other projects, project creation, billing and service-key management are excluded. The permissions below apply within that restriction and your current role.</p></>
        : <p className="muted small">This covers the selected workspace, rather than one linked project. Your current role also limits access.</p>}
      <p className="muted">
        Approve only a request you started. Check the client, project or workspace, permissions and return address before continuing.
      </p>

      <ul className="consent-scopes">
        {request.scopes.map((s) => (
          <li key={s}>{oauthScopeWords(s, request.project ? 'project' : 'workspace')}</li>
        ))}
      </ul>

      {/* Shown because a look-alike redirect is what a stolen grant needs. */}
      <p className="muted small">
        You will be sent back to <code>{new URL(request.redirect_uri).origin}</code>
      </p>

      <div className="row">
        <button type="button" className="btn btn-primary" onClick={() => void decide(true)} disabled={deciding || request.organization_id !== active?.id || request.reviewedSearch !== search}>
          {deciding ? 'Working…' : 'Approve'}
        </button>
        <button type="button" className="btn" onClick={() => void decide(false)} disabled={deciding}>
          Cancel
        </button>
      </div>
    </div>
  )
}
